/* Chill Country — Bird Hunt
 * Phaser 3 single-file game. Loads asset URLs from window.GAME_ASSETS so it
 * works both locally (relative paths) and on Shopify ({{ ... | asset_url }}).
 */
(function () {
  'use strict';

  // --------- Constants ---------
  const TOTAL_BIRDS   = 30;
  const SHOT_COOLDOWN = 200; // ms

  const NAVY  = '#1a2744';
  const SAND  = '#ffffff';
  const CORAL = '#e8927c';
  const FONT  = 'Georgia, serif';

  const TIERS = [
    { threshold: 5,  label: '10% OFF', pct: 10, reward_id: 'TIER_1', reward_label: '10% off' },
    { threshold: 15, label: '15% OFF', pct: 15, reward_id: 'TIER_2', reward_label: '15% off' },
    { threshold: 30, label: '20% OFF', pct: 20, reward_id: 'TIER_3', reward_label: '20% off' },
  ];

  // ── Telemetry (PR 1 — Arcade migration) ────────────────────────────────
  // Best-effort, batched event ingest to /api/arcade/bird-hunt/telemetry.
  // No retries on failure; queue is bounded by flush size + interval. Schema
  // mirrors BIRD_HUNT_EVENT_TYPES in src/lib/arcade/bird-hunt-events.ts —
  // drift becomes a build-time grep per spec decision #6.
  const TELEMETRY_ENDPOINT = 'https://dashboard.chillcountry.com/api/arcade/bird-hunt/telemetry';
  const TELEMETRY_FLUSH_SIZE = 10;
  const TELEMETRY_FLUSH_INTERVAL_MS = 5000;
  const ALLOWED_EVENT_TYPES = new Set([
    'page_loaded','game_started','bird_hit','tier_reached',
    'golden_bird_hit','golden_bird_missed','game_completed',
    'email_submitted','email_skipped','mint_request_sent',
    'mint_success','mint_failed','restart',
  ]);

  function arcadeSessionId() {
    let s = null;
    try { s = sessionStorage.getItem('arcade_bird_hunt_session'); } catch (_) {}
    if (!s) {
      s = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : ('s-' + Date.now() + '-' + Math.random().toString(36).slice(2));
      try { sessionStorage.setItem('arcade_bird_hunt_session', s); } catch (_) {}
    }
    return s;
  }
  function arcadeClientId() {
    let s = null;
    try { s = localStorage.getItem('arcade_client_id'); } catch (_) {}
    if (!s) {
      s = (window.crypto && crypto.randomUUID) ? crypto.randomUUID() : ('c-' + Date.now() + '-' + Math.random().toString(36).slice(2));
      try { localStorage.setItem('arcade_client_id', s); } catch (_) {}
    }
    return s;
  }
  async function sha256Hex(text) {
    if (!(window.crypto && crypto.subtle)) return '';
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  let telemetryQueue = [];
  let telemetryTimer = null;

  function sendTelemetry(event_type, properties) {
    if (!ALLOWED_EVENT_TYPES.has(event_type)) {
      console.warn('[arcade] unknown event_type:', event_type);
      return;
    }
    telemetryQueue.push({ event_type: event_type, occurred_at_ms: Date.now(), properties: properties || {} });
    if (telemetryQueue.length >= TELEMETRY_FLUSH_SIZE) {
      flushTelemetry();
    } else if (!telemetryTimer) {
      telemetryTimer = setTimeout(flushTelemetry, TELEMETRY_FLUSH_INTERVAL_MS);
    }
  }

  function flushTelemetry(opts) {
    if (telemetryTimer) { clearTimeout(telemetryTimer); telemetryTimer = null; }
    if (telemetryQueue.length === 0) return;
    const payload = JSON.stringify({
      session_id: arcadeSessionId(),
      client_id:  arcadeClientId(),
      events:     telemetryQueue.splice(0, 50),
    });
    const useBeacon = opts && opts.unload && typeof navigator.sendBeacon === 'function';
    if (useBeacon) {
      try { navigator.sendBeacon(TELEMETRY_ENDPOINT, new Blob([payload], { type: 'application/json' })); } catch (_) {}
    } else {
      try {
        fetch(TELEMETRY_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        }).catch(function () { /* best-effort */ });
      } catch (_) { /* best-effort */ }
    }
  }

  window.addEventListener('pagehide', function () { flushTelemetry({ unload: true }); });
  window.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushTelemetry({ unload: true });
  });

  // Render the logo as a native <img> overlaid on the canvas so the browser
  // scales it crisply at any device pixel ratio. Phaser's WebGL renderer
  // bakes images into a fixed-DPR canvas, which looks soft on retina screens.
  function showHTMLLogo(cx, cy, w, h) {
    const el = document.getElementById('bird-hunt-logo');
    if (!el) return;
    el.src = window.GAME_ASSETS.logoLight;
    el.style.left    = (cx - w / 2) + 'px';
    el.style.top     = (cy - h / 2) + 'px';
    el.style.width   = w + 'px';
    el.style.height  = h + 'px';
    el.style.display = 'block';
  }
  function hideHTMLLogo() {
    const el = document.getElementById('bird-hunt-logo');
    if (el) el.style.display = 'none';
  }

  function showStartScreen() {
    const el = document.getElementById('start-screen-overlay');
    if (el) el.classList.remove('hidden');
  }
  function hideStartScreen() {
    const el = document.getElementById('start-screen-overlay');
    if (el) el.classList.add('hidden');
  }

  function showPauseScreen() {
    const el = document.getElementById('pause-screen-overlay');
    if (el) el.classList.remove('hidden');
  }
  function hidePauseScreen() {
    const el = document.getElementById('pause-screen-overlay');
    if (el) el.classList.add('hidden');
  }

  function showWinScreen() {
    const el = document.getElementById('win-screen-overlay');
    if (el) el.classList.remove('hidden');
  }
  function hideWinScreen() {
    const el = document.getElementById('win-screen-overlay');
    if (el) el.classList.add('hidden');
  }

  // ----- HUD (HTML overlay; see #game-hud in duck-hunt-game.liquid) -----
  // GameScene drives these helpers instead of drawing on the Phaser canvas so
  // text + buttons stay crisp at any device pixel ratio.
  function showHUD() {
    const el = document.getElementById('game-hud');
    if (el) el.classList.remove('hidden');
  }
  function hideHUD() {
    const el = document.getElementById('game-hud');
    if (el) el.classList.add('hidden');
  }
  function showCountdown(text) {
    const overlay = document.getElementById('countdown-overlay');
    const tx      = document.getElementById('countdown-text');
    if (tx) tx.textContent = text;
    if (overlay) overlay.classList.remove('hidden');
  }
  function hideCountdown() {
    const overlay = document.getElementById('countdown-overlay');
    if (overlay) overlay.classList.add('hidden');
  }
  function setHUDCounter(birdsHit, total) {
    const el = document.getElementById('hud-counter');
    if (el) el.textContent = birdsHit + ' / ' + total;
  }
  function setFooterProgress(pct) {
    const el = document.getElementById('footer-progress-fill');
    if (el) el.style.width = (Math.max(0, Math.min(1, pct)) * 100) + '%';
  }
  function setFooterReward(label) {
    const el = document.getElementById('footer-reward');
    if (!el) return;
    if (label) {
      el.textContent = label;
      el.classList.remove('hidden');
    } else {
      // Clear text + tier-classes immediately so a stale reward label
      // from the previous game doesn't briefly flash through during
      // the 500ms opacity transition when a new game starts (or any
      // hide path). The opacity fade still runs, but with no visible
      // text content there's nothing to flash.
      el.textContent = '';
      el.classList.remove('just-earned', 'is-golden');
      el.classList.add('hidden');
    }
  }
  function setFooterHintVisible(visible) {
    const el = document.getElementById('footer-hint');
    if (el) el.classList.toggle('hidden', !visible);
  }
  function blinkFooterReward() {
    const el = document.getElementById('footer-reward');
    if (!el) return;
    el.classList.remove('just-earned');
    // Force reflow so the animation restarts even when the class was
    // already present (e.g. consecutive tier achievements).
    void el.offsetWidth;
    el.classList.add('just-earned');
  }
  function setFooterGolden(enabled) {
    const fill   = document.getElementById('footer-progress-fill');
    const reward = document.getElementById('footer-reward');
    if (fill)   fill.classList.toggle('is-golden', enabled);
    if (reward) reward.classList.toggle('is-golden', enabled);
  }
  function setFooterProgressVisible(visible) {
    const el = document.getElementById('footer-progress');
    if (el) el.classList.toggle('hidden', !visible);
  }
  function setLoadingProgress(pct) {
    const el = document.getElementById('loading-progress-fill');
    if (el) el.style.width = (Math.max(0, Math.min(1, pct)) * 100) + '%';
  }
  function hideLoadingScreen() {
    const el = document.getElementById('loading-screen');
    if (el) el.classList.add('hidden');
  }
  function setHUDPauseBtn(paused) {
    const el = document.getElementById('ctrl-pause');
    if (!el) return;
    el.dataset.state = paused ? 'paused' : 'playing';
    el.setAttribute('aria-label', paused ? 'Resume' : 'Pause');
  }
  function setHUDMuteBtn(muted) {
    const el = document.getElementById('ctrl-mute');
    if (!el) return;
    el.dataset.state = muted ? 'off' : 'on';
    el.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
  }
  // Show/hide the "Turn Sound On" prompt on the hill during the start
  // screen. Pulsating button — when clicked, calls the active scene's
  // toggleMute() which unlocks the AudioContext and starts the
  // start-screen music in one go. Hide fades the prompt out over
  // ~400ms (smooth dismiss) before removing it from layout.
  function setStartSoundHint(visible) {
    const el = document.getElementById('start-sound-hint');
    if (!el) return;
    if (visible) {
      el.classList.remove('hidden', 'fading-out');
    } else if (!el.classList.contains('hidden')) {
      el.classList.add('fading-out');
      setTimeout(() => el.classList.add('hidden'), 400);
    }
  }
  // The active GameScene instance is tracked at module scope so the HTML
  // button handlers — bound once — always route to the latest scene after
  // restarts.
  let activeScene = null;
  function bindHUDControls() {
    if (bindHUDControls._done) return;
    bindHUDControls._done = true;
    const pause = document.getElementById('ctrl-pause');
    if (pause) pause.addEventListener('click', (e) => {
      e.preventDefault();
      if (!activeScene || activeScene.gameOver) return;
      activeScene.togglePause();
    });
    const mute = document.getElementById('ctrl-mute');
    if (mute) mute.addEventListener('click', (e) => {
      e.preventDefault();
      if (!activeScene) return;
      activeScene.toggleMute();
    });
  }

  // Cloudflare Worker that mints a unique Shopify discount code per win
  // and returns it to the browser. Per-code 30-day expiry, single-use.
  // Worker source: ./worker/index.js. Leave empty to fall back to the
  // legacy "we'll email you the code" Klaviyo-only flow.
  const COUPON_WORKER_URL = 'https://bird-hunt-coupon.madison-thcprovision.workers.dev/assign-code';

  // Path the Shop Now button lands on after applying a discount. Used as
  // the redirect= param on /discount/<code>, and as the bare href in the
  // no-code fallback case.
  const SHOP_NOW_PATH = '/products/chill-countrys-fast-acting-organic-edibles-gummies';
  const SHOP_NOW_URL  = 'https://chillcountry.com' + SHOP_NOW_PATH;

  function renderWinConfirmation(confEl, code, earned) {
    // Build via DOM (not innerHTML) so the code string can't break out of
    // the surrounding markup even though the worker controls it.
    confEl.textContent = '';
    if (code) {
      // Persist the minted code so the cart drawer can label its badge
      // as "Bird Hunt" once the player adds items to their cart. Shopify's
      // cart.discount_applications doesn't always expose BOGO code titles
      // (the engine treats some as automatic discounts), so the drawer's
      // localStorage fallback is the only reliable identification channel.
      try { localStorage.setItem('cc_discount_code', code); } catch (_) { /* ignore */ }

      const codeEl = document.createElement('div');
      codeEl.className = 'win-code';
      codeEl.textContent = code;
      codeEl.setAttribute('role', 'button');
      codeEl.setAttribute('tabindex', '0');
      codeEl.setAttribute('aria-label', `Copy code ${code}`);
      codeEl.title = 'Click to copy';
      const copy = async () => {
        try {
          // Modern clipboard API; falls back silently if blocked (e.g.
          // non-secure context or browser restrictions).
          await navigator.clipboard.writeText(code);
          codeEl.classList.add('copied');
          codeEl.textContent = 'COPIED!';
          setTimeout(() => {
            codeEl.classList.remove('copied');
            codeEl.textContent = code;
          }, 1200);
        } catch (_) { /* no-op */ }
      };
      codeEl.addEventListener('click', copy);
      codeEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); copy(); }
      });
      confEl.appendChild(codeEl);

      const blurb = document.createElement('div');
      blurb.className = 'win-conf-blurb';
      blurb.textContent = earned.isGolden
        ? 'Use this code for Buy 1 Get 1 FREE bags and cases at chillcountry.com.'
        : `Use this code for ${earned.pct}% OFF your next order.`;
      confEl.appendChild(blurb);
    } else {
      const desc = earned.isGolden ? 'Buy One Get One' : `${earned.pct}% off`;
      confEl.textContent =
        `Check your inbox — your ${desc} code is on its way! 🤙`;
    }
  }

  // Configure the Shop Now button for the post-submit state.
  //
  // Hand-off: drop the visitor on the gummies product page with
  //   ?bh_apply=<code>&bh_pct=<tier>
  // where tier is the numeric percentage (10/15/20) for standard tiers
  // or the literal string "BOGO" for the golden-bird tier. The
  // cart-drawer snippet's IIFE on chillcountry.com surfaces a modal
  // (#bh-modal) showing the code + tier description + "APPLY CODE"
  // button. On click the modal fetch()-applies the discount via
  // /discount/<code> (Set-Cookie response, no navigation), writes the
  // code + tier to localStorage so the discount badge / banners
  // persist across pages, and dismisses itself in favour of the
  // static product-page banner.
  function configureShopNowButton(shopBtn, code, earned) {
    if (!shopBtn) return;
    if (code) {
      const params = new URLSearchParams({ bh_apply: code });
      if (earned) {
        params.set('bh_pct', earned.isGolden ? 'BOGO' : String(earned.pct));
      }
      shopBtn.href = 'https://chillcountry.com' + SHOP_NOW_PATH + '?' + params.toString();
    } else {
      shopBtn.href = SHOP_NOW_URL;
    }
  }

  function setupWinScreen(earned, birdsHit, onRestart) {
    const isGolden = !!(earned && earned.isGolden);
    // Tracks whether the player submitted email this game so the restart
    // hint can fire email_skipped if they bail without submitting.
    let didSubmit = false;

    const titleEl   = document.getElementById('win-title');
    const scoreEl   = document.getElementById('win-score');
    const rewardEl  = document.getElementById('win-reward');
    const subEl     = document.getElementById('win-subtitle');
    const formEl    = document.getElementById('win-form');
    const confEl    = document.getElementById('win-confirmation');
    const shopBtn   = document.getElementById('shop-now-btn');
    let   inputEl   = document.getElementById('bh-email-input');
    let   submitEl  = document.getElementById('bh-submit-btn');

    // Title + score (always shown). For the golden-bird state the score
    // line is replaced with a call-out and tinted gold.
    if (titleEl) {
      titleEl.textContent = !earned ? 'GAME OVER'
                          : isGolden ? 'GRAND PRIZE'
                          : 'YOU WIN!';
      titleEl.classList.toggle('is-golden', isGolden);
    }
    if (scoreEl) {
      scoreEl.textContent = isGolden
        ? 'You hit the golden bird!'
        : `${birdsHit} / 30 birds hit`;
      scoreEl.classList.toggle('is-golden', isGolden);
    }

    // Reward — only shown for earned states.
    if (rewardEl) {
      if (earned) {
        rewardEl.textContent = isGolden ? 'BUY 1 GET 1' : `${earned.pct}% OFF`;
        rewardEl.classList.remove('hidden');
        rewardEl.classList.toggle('is-golden', isGolden);
      } else {
        rewardEl.classList.add('hidden');
        rewardEl.classList.remove('is-golden');
      }
    }

    // Subtitle — different copy for earned vs. no-earn.
    if (subEl) {
      subEl.textContent = earned
        ? 'Enter your email to receive your code.'
        : 'Hit at least 5 birds to earn a discount. Better luck next time, partner.';
    }

    // Reset form / confirmation / shop-now to default state.
    if (formEl) formEl.classList.add('hidden');
    if (confEl) { confEl.classList.add('hidden'); confEl.textContent = ''; }
    if (shopBtn) shopBtn.classList.add('hidden');
    if (subEl) subEl.classList.remove('hidden');

    // Wire the pulsating "Tap here to play again." hint on the hill
    // with a fresh listener (clone-replace so a previous game's
    // handler doesn't fire). Visibility timing by state:
    //   - No-earn: visible immediately (the only restart CTA).
    //   - Earned, standard tier (10/15/20%): visible immediately so
    //     the player can skip the email and restart whenever.
    //   - Earned, Grand Prize: gated behind email submission so the
    //     celebratory flow plays through first.
    let freshHint = null;
    const tapHintEl = document.getElementById('win-tap-hint');
    if (tapHintEl) {
      freshHint = tapHintEl.cloneNode(true);
      tapHintEl.parentNode.replaceChild(freshHint, tapHintEl);
      freshHint.addEventListener('click', () => {
        if (earned && !didSubmit) {
          sendTelemetry('email_skipped', {
            tier_id: earned.reward_id,
            tier_label: earned.reward_label,
          });
        }
        onRestart();
      });
      const gateBehindSubmit = !!(earned && earned.isGolden);
      if (gateBehindSubmit) {
        freshHint.classList.add('hidden');
      } else {
        freshHint.classList.remove('hidden');
      }
    }

    if (earned) {
      // Earned: show email form.
      formEl.classList.remove('hidden');
      formEl.style.borderColor = '';
      inputEl.value = '';
      submitEl.disabled = false;
      submitEl.textContent = 'GET CODE';

      // Strip stale listeners (cloneNode) so a previous game's handler
      // can't fire on this game's submit. Same for the input — pressing
      // Enter inside it should trigger the submit just like clicking
      // the button, and we want a fresh keydown listener per game.
      const newSubmit = submitEl.cloneNode(true);
      submitEl.parentNode.replaceChild(newSubmit, submitEl);
      submitEl = newSubmit;
      const newInput = inputEl.cloneNode(true);
      inputEl.parentNode.replaceChild(newInput, inputEl);
      inputEl = newInput;
      inputEl.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          submitEl.click();
        }
      });

      submitEl.addEventListener('click', async () => {
        const email = inputEl.value.trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          // Indicate invalid email by tinting the form's outer border red.
          // (The input itself has no border now that it lives inside the
          // combined white pill.)
          formEl.style.borderColor = '#c23d31';
          inputEl.focus();
          return;
        }
        submitEl.disabled = true;
        submitEl.textContent = 'Sending…';

        // Telemetry: email_submitted + mint_request_sent fire before the
        // worker fetch. Raw email NEVER goes to telemetry — only the hash.
        didSubmit = true;
        const emailSha = await sha256Hex(email.toLowerCase());
        sendTelemetry('email_submitted', {
          tier_id: earned.reward_id,
          tier_label: earned.reward_label,
          email_sha256: emailSha,
        });
        sendTelemetry('mint_request_sent', { tier_id: earned.reward_id });
        const mintStart = Date.now();

        // Two submit paths:
        //  - COUPON_WORKER_URL set → POST to the Cloudflare Worker which
        //    assigns a unique Klaviyo coupon code server-side and returns
        //    it, so we can show the code in the confirmation immediately.
        //  - Empty → fallback to the original client-side Klaviyo event
        //    POST. The Klaviyo flow still emails the code, just without
        //    instant on-screen display.
        let assignedCode = null;
        if (COUPON_WORKER_URL) {
          try {
            const r = await fetch(COUPON_WORKER_URL, {
              method: 'POST',
              headers: { 'content-type': 'application/json' },
              body: JSON.stringify({
                email,
                reward_id:    earned.reward_id,
                reward_label: earned.reward_label,
              }),
            });
            if (r.ok) {
              const j = await r.json();
              if (j && j.code) assignedCode = j.code;
              sendTelemetry('mint_success', {
                tier_id: earned.reward_id,
                mint_duration_ms: Date.now() - mintStart,
              });
            } else {
              sendTelemetry('mint_failed', {
                tier_id: earned.reward_id,
                error_status: r.status,
                mint_duration_ms: Date.now() - mintStart,
              });
            }
          } catch (_) {
            sendTelemetry('mint_failed', {
              tier_id: earned.reward_id,
              error_status: 0,
              mint_duration_ms: Date.now() - mintStart,
            });
            /* fall through to plain confirmation */
          }
        } else {
          try {
            await fetch('https://a.klaviyo.com/client/events/?company_id=Rta5rN', {
              method: 'POST',
              headers: { 'content-type': 'application/json', 'revision': '2023-02-22' },
              body: JSON.stringify({ data: { type: 'event', attributes: {
                metric: { name: 'Bird Hunt Reward' },
                profile: { email },
                properties: { reward_id: earned.reward_id, reward_label: earned.reward_label },
              }}}),
            });
          } catch (_) {}
        }

        // Replace the form + subtitle with the confirmation message,
        // surface the Shop Now CTA, and (for Grand Prize, which gated
        // the tap-hint behind submission) reveal the restart hint.
        formEl.classList.add('hidden');
        if (subEl) subEl.classList.add('hidden');
        renderWinConfirmation(confEl, assignedCode, earned);
        configureShopNowButton(shopBtn, assignedCode, earned);
        confEl.classList.remove('hidden');
        if (shopBtn) shopBtn.classList.remove('hidden');
        if (freshHint) freshHint.classList.remove('hidden');
      });
    }
    // No-earn falls through — no email form, and the tap-hint was
    // already revealed up front per the gateBehindSubmit logic above.

    showWinScreen();
  }

  // Difficulty curve. Ramps faster than before so the player feels pressure
  // earlier — tier 2 starts at bird 6, tier 3 at bird 16.
  //   Tier 1 (birds 1-5):   one-at-a-time, fast warm-up.
  //   Tier 2 (birds 6-15):  up to 2 concurrent, faster + tight spawns.
  //   Tier 3 (birds 16-30): up to 3 concurrent, fast + erratic + frequent spawns.
  function tierForBirdNum(n) {
    if (n <= 5)  return 1;
    if (n <= 15) return 2;
    return 3;
  }
  function maxConcurrentForTier(t) { return t === 1 ? 1 : t === 2 ? 2 : 3; }
  function spawnIntervalForTier(t) { return t === 1 ? 800 : t === 2 ? 550 : 380; } // ms
  function getBirdSpeed(birdNum, W) {
    const ref = Math.max(W, 1000);
    if (birdNum <= 5)  return Phaser.Math.FloatBetween(ref * 0.17, ref * 0.22);
    if (birdNum <= 15) return Phaser.Math.FloatBetween(ref * 0.26, ref * 0.33);
    return Phaser.Math.FloatBetween(ref * 0.375, ref * 0.47);
  }

  // Natural pixel dims of scene-center (1920×1801) — the gate hero element.
  const SCENE_NATURAL_W = 1920;
  const SCENE_NATURAL_H = 1801;
  // Side fence panels (LEFT 1, LEFT_repeat, RIGHT 1, RIGHT_repeat) are
  // shorter strips: 1920×695. They sit at the bottom of the viewport
  // alongside the gate.
  const SIDE_NATURAL_H  = 695;
  // scene-sky is the cloud band (1920×800), faded at top/bottom for
  // seamless tiling.
  const SKY_NATURAL_H   = 800;
  // Below this viewport width the layout switches to mobile: the two outer
  // repeat tiles (LEFT_repeat, RIGHT_repeat) are hidden. The single side
  // panels (LEFT 1, RIGHT 1) and the center gate stay visible.
  const MOBILE_BREAKPOINT = 768;
  // Minimum displayed height (px) for center gate and side panels.
  const MIN_PANEL_H = 500;
  // Fixed render scale for the sky tile — independent of sceneScale so
  // clouds stay the same visual size regardless of viewport size.
  const SKY_TILE_SCALE = 0.45;

  // --------- Shared scene helpers ---------
  // Used by both StartScene and GameScene so the sky/clouds/scale logic
  // stays in one place.
  function computeSceneScale(W, H) {
    // Height factor caps how much of the canvas the gate/hammock illustration
    // can occupy. Lower = smaller graphic = more visible sky above the sun.
    const widthScale  = (W * 0.30) / SCENE_NATURAL_W;
    const heightScale = (H * 0.50) / SCENE_NATURAL_H;
    const minScale    = MIN_PANEL_H / SCENE_NATURAL_H;
    return Math.min(heightScale, Math.max(Math.min(widthScale, heightScale), minScale));
  }

  function createSunsetGradient(scene, W, H) {
    const KEY = 'sunset-gradient';
    if (!scene.textures.exists(KEY)) {
      const texH = 512;
      const tex = scene.textures.createCanvas(KEY, 1, texH);
      const ctx = tex.getContext();
      const grad = ctx.createLinearGradient(0, 0, 0, texH);
      grad.addColorStop(0.00, '#1a2744');
      grad.addColorStop(0.25, '#c8785a');
      grad.addColorStop(0.50, '#e8927c');
      grad.addColorStop(0.75, '#f2aa7a');
      grad.addColorStop(1.00, '#f5c99a');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 1, texH);
      tex.refresh();
    }
    return scene.add.image(0, 0, KEY)
      .setOrigin(0, 0).setDisplaySize(W, H).setDepth(0);
  }

  function createCloud(scene, W, H) {
    const key   = `cloud-${Phaser.Math.Between(1, 5)}`;
    const scale = Phaser.Math.FloatBetween(0.5, 1.2);
    const y     = Phaser.Math.FloatBetween(H * 0.08, H * 0.60);
    const sprite = scene.add.image(W + 100, y, key)
      .setOrigin(0, 0.5).setScale(scale).setDepth(1);
    return { sprite, vx: Phaser.Math.FloatBetween(10, 25) };
  }

  function createClouds(scene, W, H) {
    const clouds = [];
    const TOTAL = 9;
    for (let i = 0; i < TOTAL; i++) {
      const cloud = createCloud(scene, W, H);
      cloud.sprite.x = Phaser.Math.Between(-cloud.sprite.displayWidth, W);
      clouds.push(cloud);
    }
    return clouds;
  }

  // Persist clouds across the StartScene → GameScene handoff so they
  // don't jump to fresh random positions when the new scene mounts.
  // Captured in beginGame just before scene.start, consumed once in
  // GameScene.buildScene, then cleared so subsequent restarts still
  // get a fresh cloud layout.
  let cloudsSnapshot = null;
  function snapshotClouds(clouds) {
    if (!clouds) return null;
    return clouds.map(c => ({
      key: c.sprite.texture.key,
      x: c.sprite.x,
      y: c.sprite.y,
      scale: c.sprite.scaleX,
      vx: c.vx,
    }));
  }
  function restoreClouds(scene, snapshot) {
    return snapshot.map(data => {
      const sprite = scene.add.image(data.x, data.y, data.key)
        .setOrigin(0, 0.5).setScale(data.scale).setDepth(1);
      return { sprite, vx: data.vx };
    });
  }

  function tickClouds(scene, clouds, delta) {
    if (!clouds) return;
    const W = scene.scale.width;
    const H = scene.scale.height;
    const dt = delta / 1000;
    for (const c of clouds) {
      c.sprite.x -= c.vx * dt;
      if (c.sprite.x + c.sprite.displayWidth < 0) {
        c.sprite.x = W + Phaser.Math.Between(0, 200);
        c.sprite.y = Phaser.Math.FloatBetween(H * 0.08, H * 0.60);
        c.vx = Phaser.Math.FloatBetween(10, 25);
      }
    }
  }

  // --------- BootScene ---------
  class BootScene extends Phaser.Scene {
    constructor() { super('BootScene'); }

    preload() {
      const A = window.GAME_ASSETS || {};

      // Drive the DOM #loading-screen progress bar from Phaser's loader,
      // then hide the screen once every asset has finished downloading.
      this.load.on('progress', setLoadingProgress);
      this.load.on('complete', hideLoadingScreen);

      // Bird frames
      this.load.image('bird-a-1', A.birdA1);
      this.load.image('bird-a-2', A.birdA2);
      this.load.image('bird-a-3', A.birdA3);
      this.load.image('bird-a-4', A.birdA4);
      this.load.image('bird-a-5', A.birdA5);
      this.load.image('bird-b-1', A.birdB1);
      this.load.image('bird-b-2', A.birdB2);
      this.load.image('bird-b-3', A.birdB3);
      this.load.image('bird-b-4', A.birdB4);
      this.load.image('bird-b-5', A.birdB5);
      // Scene
      this.load.image('scene-sky',          A.sceneSky);
      this.load.image('scene-center',       A.sceneCenter);
      this.load.image('scene-left-1',       A.sceneLeft1);
      this.load.image('scene-left-repeat',  A.sceneLeftRepeat);
      this.load.image('scene-right-1',      A.sceneRight1);
      this.load.image('scene-right-repeat', A.sceneRightRepeat);
      // Overlays
      this.load.image('crosshair', A.crosshair);
      this.load.image('feather',   A.feather);
      // Audio
      if (A.sfxShot)       this.load.audio('sfx-shot',         A.sfxShot);
      if (A.sfxHit)        this.load.audio('sfx-hit',          A.sfxHit);
      if (A.sfxEscape)     this.load.audio('sfx-escape',       A.sfxEscape);
      if (A.sfxWin)        this.load.audio('sfx-win',          A.sfxWin);
      if (A.sfxStartMusic) this.load.audio('sfx-start-music',  A.sfxStartMusic);
      if (A.sfxGameMusic)  this.load.audio('sfx-game-music',   A.sfxGameMusic);
      if (A.sfxTier)       this.load.audio('sfx-tier',         A.sfxTier);
      if (A.sfxGoldEnter)  this.load.audio('sfx-gold-enter',   A.sfxGoldEnter);
      if (A.sfxGoldHit)    this.load.audio('sfx-gold-hit',     A.sfxGoldHit);
      if (A.sfxLoser)      this.load.audio('sfx-loser',        A.sfxLoser);
      if (A.sfxWinner)     this.load.audio('sfx-winner',       A.sfxWinner);
      if (A.sfxGrandPrize) this.load.audio('sfx-grand-prize',  A.sfxGrandPrize);
      if (A.logoDark)  this.load.image('logo-dark',  A.logoDark);
      if (A.logoLight) this.load.image('logo-larger2', A.logoLight);

      const W = this.scale.width, H = this.scale.height;
      this.add.text(W / 2, H / 2, 'Loading…', {
        fontFamily: FONT, fontSize: '28px', color: NAVY,
      }).setOrigin(0.5);
    }

    create() {
      this.anims.create({
        key: 'bird-a-fly',
        frames: [
          { key: 'bird-a-1' }, { key: 'bird-a-2' },
          { key: 'bird-a-3' }, { key: 'bird-a-4' },
        ],
        frameRate: 8,
        repeat: -1,
      });
      this.anims.create({
        key: 'bird-b-fly',
        frames: [
          { key: 'bird-b-1' }, { key: 'bird-b-2' },
          { key: 'bird-b-3' }, { key: 'bird-b-4' },
        ],
        frameRate: 8,
        repeat: -1,
      });
      this.generateCloudTextures();
      sendTelemetry('page_loaded', { time_to_load_ms: Math.round(performance.now()) });
      this.scene.start('StartScene');
    }

    // Build 5 distinct cloud textures from canvas-rendered radial-gradient
    // blobs. Each variant has hand-tuned [cx, cy, rx, ry] tuples so the
    // silhouettes read as different shapes rather than randomized noise.
    generateCloudTextures() {
      const W = 420, H = 130;
      // Each variant: array of [cx, cy, rx, ry] blob descriptors.
      // Blobs cluster horizontally (cy near center) and overlap to form an
      // elongated wispy mass.
      const variants = [
        // 1: long thin streak — soft wispy
        [
          [55, 70, 50, 28],
          [125, 60, 65, 38],
          [200, 65, 80, 42],
          [275, 70, 70, 38],
          [340, 65, 55, 32],
          [395, 70, 35, 22],
        ],
        // 2: chunky left, tapering right
        [
          [70, 60, 75, 50],
          [160, 65, 70, 45],
          [240, 70, 60, 38],
          [310, 75, 50, 32],
          [375, 75, 38, 25],
        ],
        // 3: lumpy three-bump cumulus
        [
          [55, 75, 50, 32],
          [115, 55, 60, 40],
          [175, 75, 55, 32],
          [240, 55, 65, 42],
          [305, 75, 55, 35],
          [365, 60, 55, 38],
          [405, 70, 32, 22],
        ],
        // 4: flat stretched stratus
        [
          [80, 65, 70, 28],
          [165, 65, 80, 32],
          [250, 65, 80, 30],
          [335, 65, 65, 28],
          [398, 65, 38, 22],
        ],
        // 5: asymmetric — fat right, thin tail left
        [
          [50, 72, 35, 22],
          [110, 65, 55, 32],
          [180, 62, 70, 42],
          [255, 58, 80, 48],
          [330, 62, 70, 42],
          [392, 68, 42, 28],
        ],
      ];

      for (let i = 0; i < variants.length; i++) {
        const key = `cloud-${i + 1}`;
        if (this.textures.exists(key)) this.textures.remove(key);
        const canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        const ctx = canvas.getContext('2d');

        for (const [cx, cy, rx, ry] of variants[i]) {
          const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, rx);
          grad.addColorStop(0,   'rgba(255,245,235, 0.32)');
          grad.addColorStop(0.5, 'rgba(255,240,225, 0.12)');
          grad.addColorStop(1,   'rgba(255,235,215, 0)');
          ctx.save();
          ctx.scale(1, ry / rx);
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(cx, cy * (rx / ry), rx, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }

        this.textures.addCanvas(key, canvas);
      }
    }
  }

  // --------- Bird ---------
  class Bird {
    constructor(scene, birdNum) {
      this.scene = scene;
      this.birdNum = birdNum;
      const W = scene.scale.width;
      const H = scene.scale.height;

      // Bird type determines flight direction:
      //   A naturally faces right → always flies left → right
      //   B naturally faces left  → always flies right → left
      const type = Phaser.Math.Between(0, 1) === 0 ? 'a' : 'b';
      this.fromLeft = (type === 'a');

      const startX = this.fromLeft ? -150 : W + 150;
      // Spawn below the HUD bar so birds never render behind it. Keep in
      // sync with --header-h in duck-hunt-game.liquid (152px desktop /
      // 104px mobile); the +80 buffer accounts for half a bird's height.
      const HEADER_H = W < 600 ? 104 : 152;
      const minY = Math.max(HEADER_H + 80, H * 0.20);
      const maxY = Math.max(minY + 40, scene.floorY - 60);
      const startY = Phaser.Math.Between(minY, maxY);

      const firstFrame = `bird-${type}-1`;
      this.sprite = scene.add.image(startX, startY, firstFrame).setDepth(20);
      this.sprite.setScale(0.45);

      this.frames = [`bird-${type}-1`, `bird-${type}-2`, `bird-${type}-3`, `bird-${type}-4`];
      this.hitFrame = `bird-${type}-5`;
      this.frameIndex = 0;
      this.frameTimer = scene.time.addEvent({
        delay: 110, callback: this.nextFrame, callbackScope: this, loop: true,
      });

      this.speed = getBirdSpeed(birdNum, W);
      this.vx = this.fromLeft ? this.speed : -this.speed;
      this.vy = 0;
      this.alive = true;
      this.hit = false;
      this.falling = false;

      // Tier 3 birds (bird 16+) change altitude mid-flight
      this.canChangeDir = birdNum > 15;
      this.dirChangeTimer = null;
      if (this.canChangeDir) this.scheduleDirectionChange(scene);
    }

    nextFrame() {
      if (this.hit || (this.scene && this.scene.paused)) return;
      this.frameIndex = (this.frameIndex + 1) % this.frames.length;
      this.sprite.setTexture(this.frames[this.frameIndex]);
    }

    scheduleDirectionChange(scene) {
      this.dirChangeTimer = scene.time.addEvent({
        delay: Phaser.Math.Between(450, 1100),
        callback: () => {
          if (!this.hit && !this.falling && !scene.paused) {
            this.vy = this.vy === 0 ? (Phaser.Math.Between(0, 1) ? -90 : 90) : 0;
          }
          if (!this.hit && !this.falling) this.scheduleDirectionChange(scene);
        },
      });
    }

    update(delta) {
      if (!this.alive) return;
      if (this.scene && this.scene.paused) return;
      const dt = delta / 1000;
      this.sprite.x += this.vx * dt;
      this.sprite.y += this.vy * dt;

      if (this.falling) {
        this.sprite.angle += 8;
        this.sprite.alpha -= 0.03;
        if (this.sprite.alpha <= 0) this.alive = false;
      }
    }

    onHit() {
      this.hit = true;
      this.falling = true;
      this.vx = 0;
      this.vy = 120;
      this.sprite.setTexture(this.hitFrame);
      if (this.frameTimer) this.frameTimer.remove();
      if (this.dirChangeTimer) this.dirChangeTimer.remove();
    }

    isOffScreen(W) {
      return this.sprite.x < -200 || this.sprite.x > W + 200 || this.sprite.y > 2000;
    }

    // True when this bird should be removed from the scene.
    shouldRemove(W) {
      if (this.falling && !this.alive) return true; // fade complete
      if (!this.hit && this.isOffScreen(W)) return true; // escaped
      return false;
    }

    destroy() {
      if (this.frameTimer) this.frameTimer.remove();
      if (this.dirChangeTimer) this.dirChangeTimer.remove();
      if (this.sprite) this.sprite.destroy();
      this.alive = false;
    }
  }

  // --------- GoldenBird ---------
  class GoldenBird {
    constructor(scene) {
      this.scene = scene;
      const W = scene.scale.width;
      const H = scene.scale.height;

      const fromLeft = Phaser.Math.Between(0, 1) === 0;
      this.fromLeft = fromLeft;
      const type = fromLeft ? 'a' : 'b';

      const startX = fromLeft ? -150 : W + 150;
      const minY = Math.max(160, H * 0.15);
      const maxY = Math.max(minY + 40, scene.floorY - 60);
      const startY = Phaser.Math.Between(minY, maxY);

      // Glow: pulsing radial behind the sprite
      this.glow = scene.add.graphics().setDepth(19);
      this._drawGlow(startX, startY);
      scene.tweens.add({
        targets: this.glow,
        alpha: { from: 0.55, to: 0.15 },
        duration: 1200,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      this.frames = [`bird-${type}-1`, `bird-${type}-2`, `bird-${type}-3`, `bird-${type}-4`];
      this.hitFrame = `bird-${type}-5`;
      this.frameIndex = 0;
      this.sprite = scene.add.image(startX, startY, this.frames[0])
        .setDepth(20)
        .setScale(0.32)
        .setTintFill(0xFFD700);

      this.frameTimer = scene.time.addEvent({
        delay: 90,
        callback: this.nextFrame,
        callbackScope: this,
        loop: true,
      });

      // Speed: matches tier-3 bird range (birds 16-30)
      const ref = Math.max(W, 1000);
      this.speed = Phaser.Math.FloatBetween(ref * 0.375, ref * 0.47);
      this.vx = fromLeft ? this.speed : -this.speed;
      this.vy = 0;

      this.alive = true;
      this.hit = false;
      this.falling = false;
      this.zigTime = 0;
    }

    _drawGlow(x, y) {
      this.glow.clear();
      const g = this.glow;
      // Three concentric circles fading out — simulates radial glow
      g.fillStyle(0xFFD700, 0.35); g.fillCircle(x, y, 42);
      g.fillStyle(0xFFE066, 0.25); g.fillCircle(x, y, 30);
      g.fillStyle(0xFFFBCC, 0.20); g.fillCircle(x, y, 18);
    }

    nextFrame() {
      if (this.hit || (this.scene && this.scene.paused)) return;
      this.frameIndex = (this.frameIndex + 1) % this.frames.length;
      this.sprite.setTexture(this.frames[this.frameIndex]);
    }

    update(delta) {
      if (!this.alive) return;
      if (this.scene && this.scene.paused) return;
      const dt = delta / 1000;
      this.zigTime += delta;
      this.vy = Math.sin(this.zigTime / 420) * 110;
      this.sprite.x += this.vx * dt;
      this.sprite.y += this.vy * dt;
      this._drawGlow(this.sprite.x, this.sprite.y);

      if (this.falling) {
        this.sprite.angle += 8;
        this.sprite.alpha -= 0.03;
        this.glow.alpha -= 0.03;
        if (this.sprite.alpha <= 0) this.alive = false;
      }
    }

    onHit() {
      this.hit = true;
      this.falling = true;
      this.vx = 0;
      this.vy = 120;
      this.sprite.setTexture(this.hitFrame);
      if (this.frameTimer) this.frameTimer.remove();
    }

    isOffScreen(W) {
      return this.sprite.x < -300 || this.sprite.x > W + 300 || this.sprite.y > 2000;
    }

    shouldRemove(W) {
      if (this.falling && !this.alive) return true;
      if (!this.hit && this.isOffScreen(W)) return true;
      return false;
    }

    destroy() {
      if (this.frameTimer) this.frameTimer.remove();
      if (this.sprite) this.sprite.destroy();
      if (this.glow) this.glow.destroy();
      this.alive = false;
    }
  }

  // --------- StartScene ---------
  // Title screen: sunset gradient + drifting clouds + the scene-center gate
  // anchored to the bottom, with title, reward tiers, and SHOOT BIRDS button.
  // Fully responsive — every element is positioned/sized as a fraction of
  // the live viewport, and layoutScene() rebuilds on every resize event.
  class StartScene extends Phaser.Scene {
    constructor() { super('StartScene'); }

    create() {
      this.children.removeAll();
      this._starting   = false;
      this.paused      = false;   // birds rely on scene.paused
      this.gameOver    = false;   // hud click handler reads this
      // Default the page to muted. Browser autoplay policy blocks audio
      // until the first user gesture anyway, and a visible muted icon
      // makes "click here to enable sound" obvious. Sound manager state
      // is shared across scenes, so this carries into GameScene too.
      this.muted       = true;
      this.sound.mute  = true;
      this.uiElements  = [];
      this.startBirds  = [];

      // HUD is visible during the start screen (counter at 0/30, pause icon
      // shows the "play" affordance to start). Route HTML button clicks to
      // this scene's togglePause/toggleMute via the active-scene reference.
      activeScene = this;
      setHUDCounter(0, TOTAL_BIRDS);
      setHUDPauseBtn(true);   // play-triangle icon = "tap me to start"
      setHUDMuteBtn(true);    // muted icon = "click to enable sound"
      setFooterProgress(0);
      setFooterReward(null);
      setFooterHintVisible(false);      // hint hidden until the game starts
      setFooterProgressVisible(false);  // bar only appears once the game starts
      setStartSoundHint(this.muted);    // pulsating "Turn Sound On" hint while muted
      bindHUDControls();
      showHUD();

      // Bind a fresh click listener on the sound hint each scene start
      // (clone-replace pattern so previous-game listeners can't fire).
      const hint = document.getElementById('start-sound-hint');
      if (hint) {
        const freshHint = hint.cloneNode(true);
        hint.parentNode.replaceChild(freshHint, hint);
        freshHint.addEventListener('click', () => this.toggleMute());
      }

      showStartScreen();

      // Start-screen music. Created but not played here — the scene
      // loads muted, so the music waits for the user's first click on
      // the mute button (which is the gesture that both unlocks the
      // browser AudioContext and starts playback). See toggleMute().
      if (this.cache.audio.exists('sfx-start-music')) {
        this.startMusic = this.sound.add('sfx-start-music', { loop: true, volume: 0.45 });
      }

      window.startBirdHunt = () => this.beginGame();

      this.layoutScene();
      this.scale.on('resize', this.layoutScene, this);

      // Tap anywhere on the canvas (outside HUD buttons) to start.
      this.input.on('pointerdown', () => {
        if (this._starting) return;
        this.beginGame();
      });

      // Kick off decorative bird spawning after a short delay.
      this.time.delayedCall(400, () => this.spawnStartBird());

      this.events.once('shutdown', () => {
        this.scale.off('resize', this.layoutScene, this);
        hideStartScreen();
        hideCountdown();
        setStartSoundHint(false);    // hide the hill prompt; game-scene shouldn't show it
        if (this.startMusic) {
          this.startMusic.stop();
          this.startMusic.destroy();
          this.startMusic = null;
        }
        if (this.startBirds) {
          for (const b of this.startBirds) { if (b && b.destroy) b.destroy(); }
          this.startBirds = null;
        }
        delete window.startBirdHunt;
      });
    }

    beginGame() {
      if (this._starting) return;
      this._starting = true;
      hideStartScreen();
      if (this.startMusic) {
        this.startMusic.stop();
      }
      // Fade any decorative birds still on screen so the countdown plays
      // against an empty sky. spawnStartBird also bails when _starting is
      // true so no new birds appear from here on.
      if (this.startBirds && this.startBirds.length) {
        for (const b of this.startBirds) {
          if (b && b.sprite) {
            this.tweens.add({
              targets: b.sprite,
              alpha: 0,
              duration: 350,
              ease: 'Linear',
            });
          }
        }
      }
      this.runCountdown(() => {
        hideCountdown();
        // Capture the current cloud layout so GameScene can resume them
        // in place instead of generating a fresh random layout (which
        // reads as a jump).
        cloudsSnapshot = snapshotClouds(this.clouds);
        // Hand off directly to GameScene — no camera fade-out. The two
        // scenes share the same sky + gate composition so the swap reads
        // as continuous; the 250ms fadeOut we used to have was just a
        // visible black flash between the countdown and gameplay.
        this.scene.start('GameScene');
      });
    }

    runCountdown(onDone) {
      const steps = ['3', '2', '1', 'Shoot!'];
      let i = 0;
      const tick = () => {
        if (i >= steps.length) { onDone(); return; }
        showCountdown(steps[i]);
        i += 1;
        this.time.delayedCall(800, tick);
      };
      tick();
    }

    spawnStartBird() {
      if (!this.scene || !this.scene.isActive('StartScene')) return;
      // Stop spawning once the player has tapped to start so the countdown
      // doesn't get fresh birds flying across it.
      if (this._starting) return;
      // birdNum=1: slowest tier, no mid-flight direction changes.
      const bird = new Bird(this, 1);
      this.startBirds.push(bird);
      this.time.delayedCall(Phaser.Math.Between(1200, 2400), () => this.spawnStartBird());
    }

    // HUD pause button during start screen = start the game.
    togglePause() {
      this.beginGame();
    }

    // HUD mute button during start screen. Page loads muted, so the
    // first click here is the user gesture that both unlocks the
    // browser AudioContext AND starts playback of the looped start
    // music — done in one step so sound starts the instant the icon
    // flips. Subsequent clicks toggle Phaser's global sound manager
    // mute, which carries through to GameScene as well.
    async toggleMute() {
      const ctx = this.sound.context;
      if (ctx && ctx.state !== 'running') {
        try { await ctx.resume(); } catch (_) { /* ignore */ }
      }
      this.muted = !this.muted;
      this.sound.mute = this.muted;
      if (this.startMusic && !this.muted && !this.startMusic.isPlaying) {
        try { this.startMusic.play(); } catch (_) { /* ignore */ }
      }
      setHUDMuteBtn(this.muted);
      // Keep the "Turn Sound On" hint in sync — visible while muted,
      // hidden once sound is on (whether unlocked via this hint or the
      // HUD mute button).
      setStartSoundHint(this.muted);
    }

    layoutScene() {
      // Tear down every previously-built element so the new layout starts
      // from a clean slate.
      if (this.uiElements) {
        for (const el of this.uiElements) {
          if (el && el.destroy) el.destroy();
        }
      }
      if (this.clouds) {
        for (const c of this.clouds) {
          if (c.sprite && c.sprite.destroy) c.sprite.destroy();
        }
        this.clouds = null;
      }
      if (this.gradientImg && this.gradientImg.destroy) {
        this.gradientImg.destroy();
        this.gradientImg = null;
      }
      this.uiElements = [];

      const W = this.scale.width;
      const H = this.scale.height;

      // Sky: gradient + clouds
      this.gradientImg = createSunsetGradient(this, W, H);
      this.clouds = createClouds(this, W, H);

      // Gate + fence panels — same composition as GameScene.buildScene()
      const sceneScale = computeSceneScale(W, H);
      const cx = W / 2;
      const panelW = SCENE_NATURAL_W * sceneScale;
      const sideH  = SIDE_NATURAL_H * sceneScale;
      const left1CenterX    = cx - panelW;
      const right1CenterX   = cx + panelW;
      const left1LeftEdge   = left1CenterX  - panelW / 2;
      const right1RightEdge = right1CenterX + panelW / 2;
      const leftRepW        = Math.max(0, left1LeftEdge);
      const rightRepW       = Math.max(0, W - right1RightEdge);
      const isMobile        = W < MOBILE_BREAKPOINT;

      // Left repeat tile — guard against zero-width tileSprite (Phaser
      // WebGL silently errors on 0-dim tileSprites).
      if (leftRepW > 0 && !isMobile) {
        const leftRep = this.add.tileSprite(0, H, leftRepW, sideH, 'scene-left-repeat')
          .setOrigin(0, 1).setDepth(2);
        leftRep.tileScaleX = sceneScale;
        leftRep.tileScaleY = sceneScale;
        leftRep.tilePositionX = -leftRepW / sceneScale;
        this.uiElements.push(leftRep);
      }

      // Right repeat tile — same guard
      if (rightRepW > 0 && !isMobile) {
        const rightRep = this.add.tileSprite(right1RightEdge, H, rightRepW, sideH, 'scene-right-repeat')
          .setOrigin(0, 1).setDepth(2);
        rightRep.tileScaleX = sceneScale;
        rightRep.tileScaleY = sceneScale;
        this.uiElements.push(rightRep);
      }

      // Single side fence panels (with perched birds)
      const left1 = this.add.image(left1CenterX, H, 'scene-left-1')
        .setOrigin(0.5, 1).setScale(sceneScale).setDepth(3);
      this.uiElements.push(left1);
      const right1 = this.add.image(right1CenterX, H, 'scene-right-1')
        .setOrigin(0.5, 1).setScale(sceneScale).setDepth(3);
      this.uiElements.push(right1);

      // Center gate
      const gate = this.add.image(cx, H, 'scene-center')
        .setOrigin(0.5, 1).setScale(sceneScale).setDepth(5);
      this.uiElements.push(gate);

      // Bird spawn range needs floorY (matches GameScene.computeFloorY).
      const floor = H - SIDE_NATURAL_H * sceneScale;
      this.floorY = Math.min(floor, H * 0.80);

      if (!this._starting) { this.cameras.main.fadeIn(0); }
    }

    update(time, delta) {
      tickClouds(this, this.clouds, delta);

      // Drive decorative flying birds.
      if (this.startBirds && this.startBirds.length) {
        const W = this.scale.width;
        for (let i = this.startBirds.length - 1; i >= 0; i--) {
          const b = this.startBirds[i];
          b.update(delta);
          if (b.shouldRemove(W)) {
            b.destroy();
            this.startBirds.splice(i, 1);
          }
        }
      }
    }
  }

  // --------- GameScene ---------
  class GameScene extends Phaser.Scene {
    constructor() { super('GameScene'); }

    create() {
      // Defensive: clear any leftover display objects from a prior boot
      // (race conditions during scene transitions can otherwise leave
      // stacked copies of scene-center, side panels, etc.)
      this.children.removeAll();

      activeScene = this;
      setHUDPauseBtn(false);
      // Inherit the mute state the player set on the start screen
      // (Phaser sound manager is shared across scenes).
      setHUDMuteBtn(!!this.sound.mute);

      this.birdsHit     = 0;
      this.birdsSpawned = 0;
      this.lastShot     = 0;
      this.birds        = [];
      this.tiersShown      = new Set();
      this.goldenBird        = null;
      this.goldenBirdHit     = false;
      this.goldenBirdSpawned = false;
      this.goldenBirdSpawnAt = Phaser.Math.Between(20, 30);
      this.muted           = !!this.sound.mute;
      this.paused       = false;
      this.gameOver     = false;

      // Telemetry timing + pause/mute counters folded into game_completed.
      this.gameStartedAt  = Date.now();
      this.pauseCount     = 0;
      this.pauseTotalMs   = 0;
      this.pauseStartedAt = 0;
      this.endedVia       = 'shots_exhausted';

      this.buildScene();
      this.buildUI();
      this.bindInput();
      this.bindResize();

      // Force Phaser to recompute size from the current parent container
      // after layout has fully settled — prevents wrong-dimension snapshot
      // on first paint causing oversized scene rendering.
      this.scale.refresh();

      // Gameplay loop music — starts once the GameScene takes over from
      // the countdown handoff, loops while the player is shooting, and
      // gets stopped + swapped for the end-screen track in showGameOver.
      // Respects the global sound.mute the player set on the start
      // screen (if muted, this still play()s but the sound manager
      // mutes the output; flipping mute off mid-game audibly resumes).
      if (this.cache.audio.exists('sfx-game-music')) {
        this.gameMusic = this.sound.add('sfx-game-music', { loop: true, volume: 0.45 });
        try { this.gameMusic.play(); } catch (_) { /* ignore */ }
      }

      // Tear down music + any leftover scene state on shutdown (e.g. on
      // scene.restart from PLAY AGAIN) so nothing leaks into the next run.
      this.events.once('shutdown', () => {
        if (this.gameMusic) { this.gameMusic.stop(); this.gameMusic.destroy(); this.gameMusic = null; }
        if (this.endMusic)  { this.endMusic.stop();  this.endMusic.destroy();  this.endMusic  = null; }
      });

      // Brief grace period before the first bird arrives
      sendTelemetry('game_started', {});
      this.time.delayedCall(900, () => this.spawnLoop());
    }

    // ----- Scene composition (back to front) -----
    // Depth stack: gradient (0) → clouds (1) → side fence (2) → side panels (3)
    // → center gate (5) → birds (20) → UI (100+).
    buildScene() {
      const W = this.scale.width;
      const H = this.scale.height;

      this.gradientImg = createSunsetGradient(this, W, H);
      // Restore the StartScene cloud layout if it was captured during
      // beginGame so the handoff reads as continuous. Snapshot is
      // consumed once — subsequent restarts (PLAY AGAIN) regenerate.
      if (cloudsSnapshot) {
        this.clouds = restoreClouds(this, cloudsSnapshot);
        cloudsSnapshot = null;
      } else {
        this.clouds = createClouds(this, W, H);
      }

      const sceneScale = computeSceneScale(W, H);
      this.sceneScale = sceneScale;
      this.sceneLayers = [];

      const cx = W / 2;
      const panelW = SCENE_NATURAL_W * sceneScale;
      const sideH  = SIDE_NATURAL_H * sceneScale;
      const left1CenterX  = cx - panelW;
      const right1CenterX = cx + panelW;
      const left1LeftEdge   = left1CenterX  - panelW / 2;
      const right1RightEdge = right1CenterX + panelW / 2;

      // Left repeat tile
      const leftRepW = Math.max(0, left1LeftEdge);
      const leftRep = this.add.tileSprite(0, H, leftRepW, sideH, 'scene-left-repeat')
        .setOrigin(0, 1).setDepth(2);
      leftRep.tileScaleX = sceneScale;
      leftRep.tileScaleY = sceneScale;
      leftRep.tilePositionX = -leftRepW / sceneScale;
      this.sceneLayers.push({ key: 'left-repeat', node: leftRep });

      // Right repeat tile
      const rightRepW = Math.max(0, W - right1RightEdge);
      const rightRep = this.add.tileSprite(right1RightEdge, H, rightRepW, sideH, 'scene-right-repeat')
        .setOrigin(0, 1).setDepth(2);
      rightRep.tileScaleX = sceneScale;
      rightRep.tileScaleY = sceneScale;
      this.sceneLayers.push({ key: 'right-repeat', node: rightRep });

      // Single side panels (with perched birds)
      const left1 = this.add.image(left1CenterX, H, 'scene-left-1')
        .setOrigin(0.5, 1).setScale(sceneScale).setDepth(3);
      this.sceneLayers.push({ key: 'left-1', node: left1 });
      const right1 = this.add.image(right1CenterX, H, 'scene-right-1')
        .setOrigin(0.5, 1).setScale(sceneScale).setDepth(3);
      this.sceneLayers.push({ key: 'right-1', node: right1 });

      // Center hero (gate)
      const center = this.add.image(cx, H, 'scene-center')
        .setOrigin(0.5, 1).setScale(sceneScale).setDepth(5);
      this.sceneLayers.push({ key: 'center', node: center });

      // Mobile: hide repeat tiles only
      const isMobile = W < MOBILE_BREAKPOINT;
      if (isMobile) {
        leftRep.setVisible(false);
        rightRep.setVisible(false);
      }

      this.floorY = this.computeFloorY(W, H, sceneScale);
    }

    // Sky gradient / cloud generation live in module-level helpers
    // (createSunsetGradient, createClouds, tickClouds) so StartScene can
    // share the same sky.

    computeFloorY(W, H, sceneScale) {
      const floor = H - SIDE_NATURAL_H * sceneScale;
      return Math.min(floor, H * 0.80);
    }

    // ----- UI -----
    buildUI() {
      const W = this.scale.width;
      const H = this.scale.height;
      this.isMobile = W < 600;

      // Pause/mute buttons are HTML — see #ctrl-pause / #ctrl-mute in
      // duck-hunt-game.liquid. Bind once; click handlers route through the
      // module-level activeScene reference.
      bindHUDControls();

      this.buildProgressBar();
      this.buildPauseOverlay();

      this.isTouch = !!(this.sys.game.device.input.touch && !this.sys.game.device.input.mouse);
      if (!this.isTouch) {
        this.crosshairImg = this.add.image(W / 2, 200, 'crosshair').setDepth(200);
        this.input.setDefaultCursor('none');
      }
    }

    // HUD is HTML — see #game-hud in duck-hunt-game.liquid. Refresh DOM state
    // for a fresh game (incl. after restart) and reveal the bar.
    buildProgressBar() {
      setHUDCounter(this.birdsHit, TOTAL_BIRDS);
      setFooterProgress(0);
      setFooterReward(null);
      setFooterHintVisible(true);
      setFooterGolden(false);
      setFooterProgressVisible(true);
      showHUD();
    }

    updateCounter() {
      setHUDCounter(this.birdsHit, TOTAL_BIRDS);
      // Golden bird wins outright — show a full meter regardless of how
      // many regular birds the player has hit.
      setFooterProgress(this.goldenBirdHit ? 1 : (this.birdsHit / TOTAL_BIRDS));

      // Earned-tier label: highest threshold the player has crossed (golden
      // bird overrides with BUY 1 GET 1 since it beats every regular tier).
      // Hint is the inverse — only visible when no tier has been earned yet.
      let earned = null;
      for (const tier of TIERS) {
        if (this.birdsHit >= tier.threshold) earned = tier;
      }
      if (this.goldenBirdHit) {
        setFooterReward('BUY 1 GET 1');
        setFooterHintVisible(false);
      } else if (earned) {
        setFooterReward(earned.label);
        setFooterHintVisible(false);
      } else {
        setFooterReward(null);
        setFooterHintVisible(true);
      }
    }

    layoutProgressBar(/* W */) {
      // No-op — HUD layout is responsive CSS; see #hud-bar in duck-hunt-game.liquid.
    }

    buildPauseOverlay() {
      // Pause UI is now HTML — see #pause-screen-overlay in duck-hunt-game.liquid
    }

    // ----- Input -----
    bindInput() {
      this.input.on('pointerdown', (pointer) => {
        if (this.gameOver) return;
        if (this.paused) { this.togglePause(); return; }

        const now = this.time.now;
        if (now - this.lastShot < SHOT_COOLDOWN) return;
        this.lastShot = now;

        this.flashEffect(pointer.x, pointer.y);
        this.playSound('sfx-shot', 0.6);

        // Find nearest bird whose hit-radius contains the shot
        let target = null;
        let bestDist = Infinity;
        for (const b of this.birds) {
          if (!b.alive || b.hit) continue;
          const dist = Phaser.Math.Distance.Between(pointer.x, pointer.y, b.sprite.x, b.sprite.y);
          const radius = b.sprite.displayWidth * 0.5;
          if (dist < radius && dist < bestDist) { target = b; bestDist = dist; }
        }
        if (target) {
          this.onBirdHit(target);
        } else if (this.goldenBird && this.goldenBird.alive && !this.goldenBird.hit) {
          const gd = this.goldenBird;
          const dist = Phaser.Math.Distance.Between(pointer.x, pointer.y, gd.sprite.x, gd.sprite.y);
          if (dist < gd.sprite.displayWidth * 0.30) this.onGoldenBirdHit();
        }
      });

      // Keyboard: SPACE to pause/resume (desktop)
      if (this.input.keyboard) {
        this.input.keyboard.on('keydown-SPACE', (event) => {
          event.preventDefault && event.preventDefault();
          if (this.gameOver) return;
          this.togglePause();
        });
      }
    }

    bindResize() {
      this.scale.on('resize', (gs) => this.repositionAll(gs.width, gs.height));
    }

    repositionAll(W, H) {
      // HUD bar + pause/mute buttons reposition via CSS media queries — see
      // #hud-bar and #hud-controls in duck-hunt-game.liquid.
      const isMobile = W < 600;
      this.isMobile = isMobile;

      // Scene
      const sceneScale = computeSceneScale(W, H);
      this.sceneScale = sceneScale;
      const cx = W / 2;
      const panelW = SCENE_NATURAL_W * sceneScale;
      const sideH  = SIDE_NATURAL_H * sceneScale;
      const left1CenterX  = cx - panelW;
      const right1CenterX = cx + panelW;
      const left1LeftEdge   = left1CenterX  - panelW / 2;
      const right1RightEdge = right1CenterX + panelW / 2;
      const leftRepW  = Math.max(0, left1LeftEdge);
      const rightRepW = Math.max(0, W - right1RightEdge);

      if (this.gradientImg) this.gradientImg.setDisplaySize(W, H);

      for (const layer of this.sceneLayers) {
        if (!layer.node) continue;
        if (layer.key === 'left-repeat') {
          layer.node.setPosition(0, H).setSize(leftRepW, sideH);
          layer.node.tileScaleX = sceneScale;
          layer.node.tileScaleY = sceneScale;
          layer.node.tilePositionX = -leftRepW / sceneScale;
          layer.node.setVisible(!isMobile);
        } else if (layer.key === 'right-repeat') {
          layer.node.setPosition(right1RightEdge, H).setSize(rightRepW, sideH);
          layer.node.tileScaleX = sceneScale;
          layer.node.tileScaleY = sceneScale;
          layer.node.setVisible(!isMobile);
        } else if (layer.key === 'left-1') {
          layer.node.setPosition(left1CenterX, H).setScale(sceneScale);
        } else if (layer.key === 'right-1') {
          layer.node.setPosition(right1CenterX, H).setScale(sceneScale);
        } else if (layer.key === 'center') {
          layer.node.setPosition(cx, H).setScale(sceneScale);
        }
      }
      this.floorY = this.computeFloorY(W, H, sceneScale);
    }

    // ----- Update loop -----
    update(time, delta) {
      // Crosshair tracks the pointer even when paused
      if (this.crosshairImg) {
        this.crosshairImg.setPosition(
          this.input.activePointer.x,
          this.input.activePointer.y
        );
      }
      // Clouds keep drifting even on pause / game over so the sky stays alive
      tickClouds(this, this.clouds, delta);
      if (this.paused || this.gameOver) return;

      const W = this.scale.width;
      // Update birds, splice out any that should be removed
      for (let i = this.birds.length - 1; i >= 0; i--) {
        const b = this.birds[i];
        b.update(delta);
        if (b.shouldRemove(W)) {
          if (!b.hit) {
            // Escaped — feather puff at last position
            this.spawnFeatherPuff(b.sprite.x, b.sprite.y);
            if (!this.gameOver) this.playSound('sfx-escape', 0.4);
          }
          b.destroy();
          this.birds.splice(i, 1);
        }
      }

      if (this.goldenBird) {
        this.goldenBird.update(delta);
        if (this.goldenBird.shouldRemove(W)) {
          if (!this.goldenBird.hit) {
            this.spawnFeatherPuff(this.goldenBird.sprite.x, this.goldenBird.sprite.y);
            this.playSound('sfx-escape', 0.4);
          }
          if (!this.goldenBird.wasHit) {
            sendTelemetry('golden_bird_missed', {
              time_into_game_ms: Date.now() - this.gameStartedAt,
            });
          }
          this.goldenBird.destroy();
          this.goldenBird = null;
        }
      }

      this.maybeGameOver();
    }

    // ----- Spawning -----
    spawnLoop() {
      if (this.gameOver || !this.scene.isActive('GameScene')) return;
      // Don't queue spawns while paused — keep checking
      if (this.paused) {
        this.time.delayedCall(300, () => this.spawnLoop());
        return;
      }
      if (this.birdsSpawned >= TOTAL_BIRDS) return;

      const nextNum = this.birdsSpawned + 1;
      const tier = tierForBirdNum(nextNum);
      if (this.birds.length < maxConcurrentForTier(tier)) {
        this.birdsSpawned = nextNum;
        this.birds.push(new Bird(this, nextNum));
        if (!this.goldenBirdSpawned && this.birdsSpawned >= this.goldenBirdSpawnAt) {
          this.goldenBirdSpawned = true;
          this.goldenBird = new GoldenBird(this);
          this.playSound('sfx-gold-enter', 0.6);
        }
      }
      this.time.delayedCall(spawnIntervalForTier(tier), () => this.spawnLoop());
    }

    onBirdHit(bird) {
      this.birdsHit++;
      sendTelemetry('bird_hit', {
        time_into_game_ms: Date.now() - this.gameStartedAt,
        is_golden: false,
        birds_hit_so_far: this.birdsHit,
      });
      bird.onHit();
      this.updateCounter();
      this.playSound('sfx-hit', 0.7);
      this.checkTier();
      this.maybeGameOver();
    }

    onGoldenBirdHit() {
      if (this.goldenBirdHit) return;
      // Mark wasHit BEFORE firing the event (per spec — guards against the
      // update-loop's off-screen cleanup racing this handler and firing a
      // spurious golden_bird_missed).
      if (this.goldenBird) this.goldenBird.wasHit = true;
      this.goldenBirdHit = true;
      const timeIntoGame = Date.now() - this.gameStartedAt;
      sendTelemetry('bird_hit', {
        time_into_game_ms: timeIntoGame,
        is_golden: true,
        birds_hit_so_far: this.birdsHit,
      });
      sendTelemetry('golden_bird_hit', { time_into_game_ms: timeIntoGame });
      this.goldenBird.onHit();
      this.playSound('sfx-hit', 0.7);
      this.playSound('sfx-gold-hit', 0.7);
      // Re-render counter so the reward label flips to "BUY 1 GET 1" via the
      // goldenBirdHit branch in updateCounter and the meter snaps to
      // 100% (also via that branch). Then dye the meter gold.
      this.updateCounter();
      setFooterGolden(true);
      blinkFooterReward();
      // Golden bird is the grand prize — game ends immediately. Match
      // the maybeGameOver pattern (900ms delay so the gold animation +
      // sparkle sfx have a moment to land before the win screen).
      this.endedVia = 'all_birds_hit';
      this.gameOver = true;
      this.time.delayedCall(900, () => this.showGameOver());
    }

    checkTier() {
      for (const tier of TIERS) {
        if (this.birdsHit >= tier.threshold && !this.tiersShown.has(tier.threshold)) {
          this.tiersShown.add(tier.threshold);
          sendTelemetry('tier_reached', {
            tier_id: tier.reward_id,
            score_at_reach: this.birdsHit,
          });
          // Footer-reward already shows the new label via updateCounter;
          // just play the arcade chime and blink it briefly for feedback.
          this.playSound('sfx-tier', 0.7);
          blinkFooterReward();
        }
      }
    }

    maybeGameOver() {
      if (this.gameOver) return;
      const allSpawnedAndGone = this.birdsSpawned >= TOTAL_BIRDS && this.birds.length === 0;
      const perfectScore = this.birdsHit >= TOTAL_BIRDS;
      if (allSpawnedAndGone || perfectScore) {
        this.endedVia = perfectScore ? 'all_birds_hit' : 'shots_exhausted';
        this.gameOver = true;
        this.time.delayedCall(900, () => this.showGameOver());
      }
    }

    showGameOver() {
      hideHUD();
      hidePauseScreen();
      if (this.goldenBird) { this.goldenBird.destroy(); this.goldenBird = null; }

      // Music handoff: stop the gameplay loop. We DON'T start the
      // end-screen background music here — instead we play the
      // win/lose sting first, then fade in the start-music loop once
      // the sting finishes so the celebratory sound has dramatic
      // breathing room before the ambient takes over.
      if (this.gameMusic) { this.gameMusic.stop(); this.gameMusic.destroy(); this.gameMusic = null; }

      let earned = null;
      for (const tier of TIERS) {
        if (this.birdsHit >= tier.threshold) earned = tier;
      }
      if (this.goldenBirdHit) {
        earned = { reward_id: 'TIER_4', reward_label: 'Buy One Get One Free', pct: 30, isGolden: true };
      }

      // Fire game_completed synchronously (flush before any UI transition).
      sendTelemetry('game_completed', {
        score: this.birdsHit,
        birds_hit: this.birdsHit,
        birds_total: TOTAL_BIRDS,
        golden_hit: !!this.goldenBirdHit,
        duration_ms: Date.now() - this.gameStartedAt,
        ended_via: this.endedVia,
        earned_tier_id: earned ? earned.reward_id : null,
        pause_count: this.pauseCount,
        pause_total_ms: this.pauseTotalMs,
        was_muted_at_end: !!this.muted,
      });
      flushTelemetry();

      const scene = this;
      const priorScore = this.birdsHit;
      const priorEarnedTierId = earned ? earned.reward_id : null;
      setupWinScreen(earned, this.birdsHit, () => {
        sendTelemetry('restart', {
          prior_score: priorScore,
          prior_earned_tier_id: priorEarnedTierId,
        });
        hideWinScreen();
        hideHUD();
        scene.scene.restart();
      });

      this.input.setDefaultCursor('');
      if (this.crosshairImg) this.crosshairImg.setVisible(false);
      // Pick the right end-screen sting based on tier, play it as a
      // one-shot, then on its 'complete' event fade in the start-music
      // as the ambient end-screen background loop.
      let stingKey = null;
      let stingVolume = 0.6;
      if (earned && earned.isGolden && this.cache.audio.exists('sfx-grand-prize')) {
        stingKey = 'sfx-grand-prize';
        stingVolume = 0.7;
      } else if (earned && this.cache.audio.exists('sfx-winner')) {
        stingKey = 'sfx-winner';
        stingVolume = 0.6;
      } else if (!earned && this.cache.audio.exists('sfx-loser')) {
        stingKey = 'sfx-loser';
        stingVolume = 0.6;
      }

      const fadeInEndMusic = () => {
        if (!this.cache.audio.exists('sfx-start-music')) return;
        this.endMusic = this.sound.add('sfx-start-music', { loop: true, volume: 0 });
        try { this.endMusic.play(); } catch (_) { return; }
        this.tweens.add({
          targets: this.endMusic,
          volume: 0.45,
          duration: 1200,
          ease: 'Linear',
        });
      };

      if (stingKey) {
        const sting = this.sound.add(stingKey, { volume: stingVolume });
        sting.once('complete', fadeInEndMusic);
        sting.play();
      } else {
        fadeInEndMusic();
      }
    }

    flashEffect(x, y) {
      const flash = this.add.circle(x, y, 18, 0xffffff, 0.9).setDepth(250);
      this.tweens.add({
        targets: flash, alpha: 0, scale: 1.8, duration: 120,
        onComplete: () => flash.destroy(),
      });
    }

    spawnFeatherPuff(x, y) {
      const W = this.scale.width;
      const H = this.scale.height;
      const px = Phaser.Math.Clamp(x, 20, W - 20);
      const py = Phaser.Math.Clamp(y, 20, H - 80);
      for (let i = 0; i < 5; i++) {
        const f = this.add.image(px, py, 'feather').setScale(0.6).setAlpha(0.8).setDepth(50);
        const angle = Phaser.Math.DegToRad(Phaser.Math.Between(0, 360));
        const dist = Phaser.Math.Between(30, 80);
        this.tweens.add({
          targets: f,
          x: px + Math.cos(angle) * dist,
          y: py + Math.sin(angle) * dist,
          alpha: 0,
          angle: Phaser.Math.Between(-180, 180),
          duration: Phaser.Math.Between(500, 900),
          onComplete: () => f.destroy(),
        });
      }
    }

    playSound(key, volume) {
      if (this.muted) return;
      if (!this.cache.audio.exists(key)) return;
      this.sound.play(key, { volume: volume == null ? 0.6 : volume });
    }

    toggleMute() {
      this.muted = !this.muted;
      this.sound.mute = this.muted;
      setHUDMuteBtn(this.muted);
    }

    togglePause() {
      this.paused = !this.paused;
      setHUDPauseBtn(this.paused);
      if (this.paused) {
        this.pauseStartedAt = Date.now();
        this.pauseCount++;
        showPauseScreen();
      } else {
        if (this.pauseStartedAt) {
          this.pauseTotalMs += Date.now() - this.pauseStartedAt;
          this.pauseStartedAt = 0;
        }
        hidePauseScreen();
      }
    }
  }

  // --------- WinScene ---------
  // Stub scene registered for future use. Game-over flow currently still
  // lives in GameScene.showGameOver().
  class WinScene extends Phaser.Scene {
    constructor() { super('WinScene'); }
  }

  // --------- Game boot ---------
  let gameStarted = false;
  function startGame() {
    if (gameStarted) return;
    gameStarted = true;
    const config = {
      type: Phaser.AUTO,
      // matches scene-center.png's hill silhouette color (sampled #082639
      // from the PNG itself) so the canvas/footer joint is invisible —
      // header + footer + canvas + hill are all exactly the same navy.
      backgroundColor: '#082639',
      parent: 'game-container',
      scene: [BootScene, StartScene, GameScene, WinScene],
      scale: { mode: Phaser.Scale.RESIZE },
    };
    new Phaser.Game(config);
  }

  function bootWhenReady() {
    if (typeof Phaser === 'undefined') {
      console.error('[bird-hunt] Phaser failed to load.');
      return;
    }
    // Wait for web fonts (Manrope) so first-render text uses the right typeface
    const fontReady = (document.fonts && document.fonts.ready)
      ? document.fonts.ready
      : Promise.resolve();
    // Wait one rAF after fonts ready so the browser has applied final
    // layout — prevents Phaser from snapshotting wrong viewport dimensions
    // and rendering scene-center oversized.
    fontReady
      .then(() => new Promise((r) => requestAnimationFrame(r)))
      .then(startGame)
      .catch(startGame);
  }

  // Wait for full window load (CSS, images, fonts) — DOMContentLoaded fires
  // before all stylesheets are applied, which can leave the parent container
  // at the wrong size when Phaser snapshots dimensions.
  if (document.readyState === 'complete') {
    bootWhenReady();
  } else {
    window.addEventListener('load', bootWhenReady, { once: true });
  }
})();
